import { useCallback, useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Tooltip, IconButton, CircularProgress,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  pageVariants, sectionVariants,
} from '../../../animations/variants';
import { getCapabilities, getOverview } from '../../../services/securityhubApi';
import { engineById } from './engines/registry';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import EnableCapabilities from './EnableCapabilities';
import { CK, sectionCardSx, statTileSx, statTileFooterSx, sectionHeadingSx, tileEntranceStyle } from '../portalStyles';
import { severityColor } from './severity';

// This is the UNION of every engine's severity vocabulary, because Overview
// renders one tile per engine from a single list. The per-engine dashboards
// (ComplianceDashboard, ThreatDashboard, MacieDashboard, …) deliberately keep
// their own narrower arrays — Macie never emits CRITICAL and only Inspector
// emits UNTRIAGED — so do NOT "sync" this array into them: a severity an engine
// cannot emit renders as a permanently-zero column on that engine's dashboard.
const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'UNTRIAGED'];

// Overview is the Security Hub umbrella landing page. If no engine is enabled
// it shows the EnableCapabilities panel; otherwise it renders one stat card per
// engine summarizing findings, in the Prism-admin ComplianceDashboard style.
export default function Overview() {
  const navigate = useNavigate();
  const [capabilities, setCapabilities] = useState(null);
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const capRes = await getCapabilities();
      const caps = capRes?.data || [];
      setCapabilities(caps);
      // Only fetch the overview when at least one engine is enabled.
      if (caps.some((c) => c.enabled)) {
        const ovRes = await getOverview();
        setOverview(ovRes?.data || []);
      }
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // The header stays mounted in every state (loading, error, loaded) so the
  // page never snaps from a bare progress bar to a full layout.
  const header = (
    <motion.div variants={sectionVariants}>
      <PortalPageHeader
        icon={<SecurityIcon sx={{ fontSize: 24 }} />}
        title="Security Hub"
        subtitle="Unified security posture across detection engines"
        action={(
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={() => load()}
                disabled={loading}
                sx={{
                  color: CK.body,
                  backgroundColor: CK.pageBg,
                  border: `1px solid ${CK.border}`,
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    backgroundColor: CK.hover,
                    borderColor: CK.blue,
                    color: CK.blue,
                  },
                }}
              >
                {loading
                  ? <CircularProgress size={20} sx={{ color: CK.blue }} />
                  : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      />
    </motion.div>
  );

  if (loading && capabilities === null) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        {header}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading security capabilities…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Take over the whole page only when there is nothing to fall back to (the
  // first load failed). Once tiles are on screen a failed Refresh must not wipe
  // them — the error is rendered inline above the grid instead.
  if (error && overview.length === 0) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        {header}
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load()} />
        </motion.div>
      </motion.div>
    );
  }

  // No engine enabled → drop into the enablement flow.
  if (capabilities && !capabilities.some((c) => c.enabled)) {
    return <EnableCapabilities />;
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {header}

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load()} />
        </motion.div>
      )}

      {/* No motion wrapper: the tiles animate themselves in pure CSS, and a
          motion component here can write an inline opacity onto them once its
          own animation settles. */}
      <Grid container spacing={3}>
          {overview.map((row, i) => {
            const engine = engineById(row.engine);
            const summary = row.summary || {};
            const color = engine?.color || '#4698D3';
            const Icon = engine?.Icon;
            const label = engine?.label || row.engine;
            const enabled = summary.enabled;
            const subtitle = [summary.status, summary.headline]
              .filter(Boolean)
              .join(' • ');

            // EnableCapabilities renders the same engine list and MUST keep the
            // same breakpoints, or the two pages lay the identical set of tiles
            // out differently. Change both or neither.
            //
            // SIZED FOR READABILITY, NOT FOR FITTING ONE ROW. These were md: 2
            // (6-up), chosen when there were five engines so all five sat on one
            // row with an empty slot, avoiding a 4 + 1 orphan. That traded the
            // wrong thing away: at 6-up a tile is a sixth of the content width,
            // and after the 48px icon and its gap the capability name has ~30px
            // — so titles truncated mid-word and the last tile clipped at the
            // container edge.
            //
            // An orphan tile on a second row is a cosmetic nit. A tile whose
            // name is unreadable defeats the tile. There are seven engines now
            // anyway, so no breakpoint keeps them on one row.
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={row.engine}>
                <div style={tileEntranceStyle(i)}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...statTileSx,
                      cursor: enabled && engine ? 'pointer' : 'default',
                      opacity: enabled ? 1 : 0.65,
                      transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      '&:hover': enabled && engine
                        ? { backgroundColor: CK.pageBg, borderColor: color }
                        : undefined,
                    }}
                    onClick={() => {
                      if (enabled && engine) navigate(engine.enableRoute);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          flexShrink: 0,
                          borderRadius: '10px',
                          backgroundColor: enabled ? `${color}12` : '#F0F0F0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: enabled ? color : CK.muted,
                        }}
                      >
                        {Icon && <Icon sx={{ fontSize: 28 }} />}
                      </Box>
                      {/* The count slot always renders so every tile's header row
                          has the same two-element structure and height. */}
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '2.5rem',
                          color: enabled ? color : CK.muted,
                          lineHeight: 1,
                        }}
                      >
                        {enabled ? (summary.totalFindings ?? 0) : '—'}
                      </Typography>
                    </Box>

                    <Typography sx={{ ...sectionHeadingSx, mb: 0.5 }}>
                      {label}
                    </Typography>

                    {row.error ? (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: CK.red,
                          fontWeight: 500,
                          wordBreak: 'break-word',
                        }}
                      >
                        {row.error}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: CK.muted,
                          fontWeight: 500,
                          wordBreak: 'break-word',
                        }}
                      >
                        {enabled ? (subtitle || 'No findings') : 'Not enabled'}
                      </Typography>
                    )}

                    {/* One footer slot keeps the severity strip and the Enable
                        button on the same baseline across a row of tiles. */}
                    <Box sx={statTileFooterSx}>
                      {enabled && summary.bySeverity && (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          {SEVERITY_ORDER.map((sev) => {
                            const n = summary.bySeverity?.[sev];
                            if (n == null) return null;
                            return (
                              <Box key={sev} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    color: severityColor(sev),
                                  }}
                                >
                                  {n}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.7rem',
                                    color: CK.muted,
                                  }}
                                >
                                  {sev.charAt(0) + sev.slice(1).toLowerCase()}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {!enabled && engine && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(engine.enableRoute);
                          }}
                        >
                          Enable
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </div>
              </Grid>
            );
          })}
      </Grid>
    </motion.div>
  );
}
